// src/api/axios.js

// Let webpack resolve Axios through its browser export. Importing the `.cjs`
// file directly makes Create React App emit it as a URL asset in production.
import {
  clearNativeAuthTokens,
  ensureNativeAccessToken,
  hasNativeRefreshToken,
  refreshNativeAccessToken,
} from "../native/auth";
import {
  getNativeApiBaseUrl,
  isNativeApp,
  NATIVE_TRANSPORT_CHANGED_EVENT,
} from "../native/runtime";
import { getNativeRegistrationRegion } from "../native/regionRouting";
import {
  recoverNativeRouteAfterTransportFailure,
  reportNativeTransportFailure,
} from "../native/regionNetworkRecovery";

import axios from "axios";
import {
  isEventCreateSandboxLocation,
  sandboxHostRating,
  sandboxOrganizerPanel,
} from "../pages/events/eventCreateSandbox";

// Пути, для которых 401/403/часть 500 считаются нормальными и НЕ вызывают редирект
const PUBLIC_AUTHLESS_PATHS = [
  "/csrf/",
  "/mobile/version-policy/",
  "/accounts/csrf/",
  "/storage/user_keys/",
  "/user_keys/",
  "/events/events_packs/",
  "/accounts/profile/",
  "/accounts/legal/",
  "/accounts/moderation/decision/",
  "/assistant/public-config/",
  "/notifications/",
  "/quests/get-tasks/",
  "/events/details/",
  "/clubs/list/",
  "/clubs/details/",
  "/events/participant-action/",
  "/events/intent/confirm/",
  "/events/user-subscriptions/",
  "/miniapp/checkin-streak/"
];

// Нормализуем URL запроса в путь без префикса /api и с ведущим слешем
function normalizePath(rawUrl) {
  try {
    const u = new URL(rawUrl, window.location.origin);
    let p = u.pathname || "/";
    // убираем возможный префикс монтирования API
    p = p.replace(/^\/api/, "");
    if (!p.startsWith("/")) p = "/" + p;
    return p;
  } catch {
    let p = String(rawUrl || "");
    if (!p.startsWith("/")) p = "/" + p;
    p = p.replace(/^\/api/, "");
    return p;
  }
}

// Точный матч по префиксу (без includes(), чтобы не ловить лишнее)
function isPublicByPath(path) {
  return PUBLIC_AUTHLESS_PATHS.some((base) => path === base || path.startsWith(base));
}

const NATIVE_PUBLIC_PATH_MAP = {
  "/accounts/register/availability/": "/accounts/native/register/availability/",
  "/accounts/social/register/availability/": "/accounts/native/social/register/availability/",
  "/accounts/register/email-code/request/": "/accounts/native/register/email-code/request/",
  "/accounts/register/email-code/verify/": "/accounts/native/register/email-code/verify/",
  "/accounts/register/photo-check/": "/accounts/native/register/photo-check/",
  "/accounts/register/": "/accounts/native/register/",
  "/accounts/password-reset/request/": "/accounts/native/password-reset/request/",
  "/accounts/password-reset/confirm/": "/accounts/native/password-reset/confirm/",
  "/accounts/google/register/": "/accounts/native/google/register/",
  "/accounts/apple/register/": "/accounts/native/apple/register/",
};

function mapNativePublicPath(rawUrl) {
  const path = normalizePath(rawUrl);
  return NATIVE_PUBLIC_PATH_MAP[path] || rawUrl;
}

const api = axios.create({
  // Base URL logic:
  // - Use REACT_APP_API_URL env variable if defined.
  // - Otherwise, use localhost for local dev.
  // - Otherwise, use "/api" for production.
  baseURL: isNativeApp()
    ? getNativeApiBaseUrl()
    : process.env.REACT_APP_API_URL
    ? process.env.REACT_APP_API_URL
    : window.location.hostname === "localhost"
      ? "http://localhost:8000"
      : "/api",
  withCredentials: !isNativeApp(),
});

// перехватчик, чтобы класть CSRF-токен в заголовок
api.interceptors.request.use(async (cfg) => {
  if (isNativeApp()) {
    cfg.baseURL = getNativeApiBaseUrl();
    cfg.withCredentials = false;
    cfg.url = mapNativePublicPath(cfg.url);
    cfg.headers["X-VaultGrid-Client"] = "native";
    cfg.headers["X-VaultGrid-Registration-Region"] = getNativeRegistrationRegion();

    if (cfg.meta?.skipNativeAuth !== true) {
      const access = await ensureNativeAccessToken();
      if (access) cfg.headers.Authorization = `Bearer ${access}`;
    }
  } else {
    const m = document.cookie.match(/csrftoken=([\w-]+)/);
    if (m) cfg.headers["X-CSRFToken"] = m[1];
  }

  try {
    const storedUiLanguage = localStorage.getItem("vg_ui_language");
    const htmlLang = document?.documentElement?.lang || "";
    const rawLanguage = String(storedUiLanguage || htmlLang || "").trim().toLowerCase();
    const normalizedLanguage = rawLanguage.includes("-")
      ? rawLanguage.split("-")[0]
      : rawLanguage;
    if (isNativeApp()) {
      cfg.headers["X-UI-Language"] = getNativeRegistrationRegion() === "RU"
        ? "ru"
        : ["sk", "cs"].includes(normalizedLanguage) ? normalizedLanguage : "en";
    } else {
      const isRussianContour =
        String(window.location?.hostname || "").trim().toLowerCase() === "ru.vaultgrid.net";
      cfg.headers["X-UI-Language"] = isRussianContour
        ? "ru"
        : ["sk", "cs"].includes(normalizedLanguage) ? normalizedLanguage : "en";
    }
  } catch {
    // ignore language header enrichment failures
  }

  // Поддержка кастомных флагов для гостевых запросов
  cfg.meta = cfg.meta || {};
  if (cfg.authOptional === true) cfg.meta.authOptional = true;
  if (cfg.suppressUnauthorizedRedirect === true) cfg.meta.suppressUnauthorizedRedirect = true;
  if (cfg.suppressGlobalError === true) cfg.meta.suppressGlobalError = true;

  // The local creator sandbox renders the real event form. Keep every write
  // inside the browser and provide only the host entitlements it needs.
  if (isEventCreateSandboxLocation()) {
    const method = String(cfg.method || "get").toLowerCase();
    const path = normalizePath(cfg.url);
    if (method !== "get" && method !== "head" && method !== "options") {
      cfg.adapter = async () => {
        throw new axios.CanceledError("The local event creation sandbox does not save changes.", cfg);
      };
    } else if ([
      "/events/get-host-rating/",
      "/events/organizer-panel/",
      "/events/organizer-panel/billing-details/",
      "/events/places-autocomplete/",
      "/events/place-details/",
    ].includes(path)) {
      const input = String(cfg.params?.input || "").trim();
      const mockData = {
        "/events/get-host-rating/": sandboxHostRating,
        "/events/organizer-panel/": sandboxOrganizerPanel,
        "/events/organizer-panel/billing-details/": {
          billing_details: { complete: true, country: "SK" },
        },
        "/events/places-autocomplete/": {
          predictions: input ? [{ place_id: "sandbox-bratislava", description: "Bratislava, Slovakia" }] : [],
        },
        "/events/place-details/": {
          status: "OK",
          address: "Bratislava, Slovakia",
          city: "Bratislava",
          country: "Slovakia",
          location: { lat: 48.1486, lng: 17.1077 },
        },
      }[path];
      cfg.adapter = async () => ({
        data: mockData,
        status: 200,
        statusText: "OK",
        headers: {},
        config: cfg,
      });
    }
  }

  return cfg;
});

if (typeof window !== "undefined") {
  window.addEventListener(NATIVE_TRANSPORT_CHANGED_EVENT, (event) => {
    api.defaults.baseURL = event?.detail?.apiOrigin || getNativeApiBaseUrl();
  });
}

// перехватчик ответов — для глобальной обработки ошибок
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (!error || !error.response) {
      if (isNativeApp()) {
        const cfg = error?.config || null;
        if (cfg && cfg._nativeRouteRetried !== true) {
          cfg._nativeRouteRetried = true;
          const recovered = await recoverNativeRouteAfterTransportFailure();
          if (recovered?.apiOrigin) {
            cfg.baseURL = recovered.apiOrigin;
            cfg.headers = cfg.headers || {};
            cfg.headers["X-VaultGrid-Registration-Region"] =
              recovered.registrationRegion;
            return api.request(cfg);
          }
        }
        reportNativeTransportFailure();
      }
      return Promise.reject(error);
    }

    const status = error.response.status;
    const cfg    = error.config || {};
    const url    = cfg.url || "";
    const meta   = cfg.meta || {};
    const method = (cfg.method || "get").toLowerCase();
    const suppressGlobalError = meta.suppressGlobalError === true;

    // --- определить публичность запроса ---
    const path = normalizePath(url);
    const isPublicRequest =
      meta.authOptional === true ||
      meta.suppressUnauthorizedRedirect === true ||
      isPublicByPath(path);

    if (
      isNativeApp() &&
      status === 401 &&
      cfg._nativeAuthRetried !== true &&
      !path.startsWith("/accounts/token/") &&
      hasNativeRefreshToken()
    ) {
      cfg._nativeAuthRetried = true;
      const access = await refreshNativeAccessToken({ force: true });
      if (access) {
        cfg.headers = cfg.headers || {};
        cfg.headers.Authorization = `Bearer ${access}`;
        return api.request(cfg);
      }

      // A timeout/offline transition while iOS is returning from a system
      // picker is not proof that the refresh token is invalid. auth.js keeps
      // the refresh token for those transient failures, so preserve the signed-
      // in session and let the next request retry instead of erasing Keychain.
      if (hasNativeRefreshToken()) {
        return Promise.reject(error);
      }
    }

    if (isNativeApp() && status === 401 && !isPublicRequest) {
      await clearNativeAuthTokens("api_unauthorized");
      window.dispatchEvent(new Event("unauthorized"));
      return Promise.reject(error);
    }

    const isAccountBlocked =
      status === 403 &&
      (
        error.response.data?.code === "account_blocked" ||
        error.response.data?.blocked === true
      );

    if (isAccountBlocked) {
      window.dispatchEvent(
        new CustomEvent("account-blocked", {
          detail: error.response.data || {},
        })
      );
      return Promise.reject(error);
    }

    if (
      status === 428 &&
      (
        error.response.data?.code === "legal_acceptance_required" ||
        error.response.data?.legal_acceptance_required === true
      )
    ) {
      window.dispatchEvent(
        new CustomEvent("legal-acceptance-required", {
          detail: error.response.data || {},
        })
      );
      return Promise.reject(error);
    }

    // --- auth-like 500 (иногда бэкенд так отвечает про «не авторизован») ---
    const isBackendAuth500 =
      status === 500 &&
      !isPublicRequest &&
      (
        error.response.data?.code === "not_authenticated" ||
        /not[_\s]?authenticated|authentication credentials/i.test(
          error.response.data?.detail || error.response.data?.error || ""
        )
      );

    // --- 401/«auth-like 500» на защищённых эндпоинтах: перепроверяем сессию ---
    if (!isNativeApp() && !isPublicRequest && (status === 401 || isBackendAuth500)) {
      // чтобы не зациклиться при повторной проверке
      if (meta.skipAuthRecheck) {
        return Promise.reject(error);
      }
      try {
        // мягкая перепроверка: не редиректим из самого чека
        await api.get("/accounts/profile/", {
          meta: {
            suppressUnauthorizedRedirect: true,
            authOptional: true,
            skipAuthRecheck: true, // этот флаг не даст зайти в этот блок повторно
          },
        });
        // Если дошли сюда — сессия валидна. Исходную ошибку пробрасываем наверх без логаута.
        return Promise.reject(error);
      } catch (recheckErr) {
        const st = recheckErr?.response?.status;
        if (st === 401) {
          // вот теперь точно нет сессии → глобальный логаут/редирект
          window.dispatchEvent(new Event("unauthorized"));
        }
        return Promise.reject(error);
      }
    }

    // --- 403 на защищённых: сессию не трогаем, только показать сообщение ---
    // ВАЖНО: для GET больше НЕ показываем глобальный "Access denied",
    // потому что много служебных GET-эндпоинтов дергаются гостями / без сессии
    // (мини-апп, IAB и т.п.). Пусть компоненты сами разбираются с 403.
    if (!isPublicRequest && status === 403 && method !== "get") {
      if (!suppressGlobalError) {
        window.dispatchEvent(
          new CustomEvent("global-error", {
            detail: { status, message: "Access denied" },
          })
        );
      }
    }

    // --- настоящие 5xx (не auth-like) на защищённых: показать баннер ---
    // GET-запросы не показываем глобально — при загрузке страниц параллельно
    // летят десятки фоновых GETов, и случайный 5xx не должен мешать пользователю.
    // Компоненты сами должны обрабатывать ошибки загрузки данных.
    // Для мутаций (POST/PUT/PATCH/DELETE) баннер оставляем — там пользователь
    // совершил действие и должен знать, что оно не прошло.
    if (!isPublicRequest && !isBackendAuth500 && status >= 500 && method !== "get") {
      console.error(`[API] ${status} error on ${method.toUpperCase()} ${url}`);
      if (!suppressGlobalError) {
        const message =
          error.response.data?.detail ||
          error.response.statusText ||
          "Server error";
        window.dispatchEvent(
          new CustomEvent("global-error", {
            detail: { status, message },
          })
        );
      }
    }

    return Promise.reject(error);
  }
);

// Profile and key snapshots are requested by several route-level components
// during the same mount. Share only the active promise: once it settles, the
// next caller performs a fresh GET, so mutations never wait on a TTL cache.
const SHARED_ACCOUNT_GET_PATHS = new Set([
  "/accounts/profile/",
  "/storage/user_keys/",
]);
const sharedAccountGets = new Map();
const rawApiGet = api.get.bind(api);

function stableParamsKey(params) {
  if (!params) return "";
  if (params instanceof URLSearchParams) return params.toString();
  if (typeof params !== "object") return String(params);

  return Object.keys(params)
    .sort()
    .map((key) => {
      const value = params[key];
      const normalized = Array.isArray(value) ? value.map(String) : String(value ?? "");
      return `${key}:${JSON.stringify(normalized)}`;
    })
    .join("|");
}

api.get = (url, config = {}) => {
  const path = normalizePath(url);
  const canShare =
    SHARED_ACCOUNT_GET_PATHS.has(path) &&
    !config?.signal &&
    !config?.cancelToken &&
    config?.meta?.disableSharedGet !== true;
  if (!canShare) return rawApiGet(url, config);

  const transport = isNativeApp()
    ? getNativeApiBaseUrl()
    : config?.baseURL || api.defaults.baseURL || "web";
  const key = `${transport}|${String(url)}|${stableParamsKey(config?.params)}`;
  const activeRequest = sharedAccountGets.get(key);
  if (activeRequest) return activeRequest;

  const request = rawApiGet(url, config);
  sharedAccountGets.set(key, request);
  const clear = () => {
    if (sharedAccountGets.get(key) === request) sharedAccountGets.delete(key);
  };
  request.then(clear, clear);
  return request;
};

export default api;   // именно export default!
